import React from 'react';
import { uuid } from '../../../CodesThatsGottaGoSomewhereElse';
import { LFC } from '../../../Legacy';
import { Path } from '../../../platform/geom/Path';
import { Polyline } from '../../../platform/geom/Polyline';
import { Rect } from '../../../platform/geom/Rect';
import { Colors } from '../../../ui';
import { EmojiStore } from '../../../ui/emoji';
import { TextAlignment, TextSettings } from '../../canvas/entities/TextConventions';
import { RenderBounds, RenderBox, RenderInner, RenderSvg, usePose } from '../../canvas/visualization/RenderHelpers';
import { RenderText } from '../../canvas/visualization/RenderText';
import { TextRuler } from '../../canvas/visualization/TextRuler';
import { Connector } from './Connector';
import { ConnectorStrokeStyle } from './ConnectorEnums';
import { ConnectorLabelTextSettings, ConnectorLayout } from './ConnectorLayout';

const ts = TextSettings.from({
    fontFamily: 'Rubik',
    fontColor: Colors.text,
    fontSize: 16,
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: 1,
    maxFontSize: 16,
    textAlignV: TextAlignment.start,
    textAlignH: TextAlignment.start,
    textMargin: 0,
});

const RenderLabelFrame = ({ area }: { area: Rect }) => {
    return <RenderBox {...area.inflate(4).toCSS()} background="#F6FAFD" border={`solid 1px ${Colors.border}`} borderRadius="4px" />;
};

export const ConnectorVisual: LFC<{ object: Connector }> = ({ object, children }) => {

    const { selected, targeted, beingEdited } = usePose(object);

    return <>
        <RenderBounds object={object}>
            <RenderInner object={object}>
                <Render
                    layout={object.layout}
                    color={object.color}
                    strokeWidth={object.strokeWidth}
                    strokeStyle={object.strokeStyle}
                    selected={selected}
                    targeted={targeted} />
                {!!object.label && <RenderLabelFrame area={object.layout.label} />}
                {!beingEdited &&
                    <RenderText
                        bounds={object.layout.label}
                        settings={ConnectorLabelTextSettings}
                        value={object.label} />
                }
            </RenderInner>
        </RenderBounds>
        {children}
    </>;
};

type RenderProps = {
    layout: ConnectorLayout,
    color: string,
    strokeWidth: number,
    strokeStyle: number,
    selected: boolean,
    targeted: boolean,
};

const emojiString = (body: Path, emoji: string) => {

    const c = EmojiStore.find(emoji)?.value;
    if (!c) return null;

    const cw = TextRuler.measureSize(`${c}${c}${c}${c}`, ts).w / 4;
    const cc = Math.round(body.length / cw);
    return Array.from({ length: cc }).map(() => c).join('');
};

const RenderCheveronMarker = ({ id, color, strokeWidth }: { id, color: string, strokeWidth: number }) =>
    <marker id={id} viewBox="0 0 10 20" refX="5" refY="10"
        markerUnits="userSpaceOnUse"
        markerWidth={strokeWidth} markerHeight={strokeWidth * 2}
        orient="auto">
        <path fill={color} d="M0.723608 1.44722C0.391157 0.782315 0.874652 0 1.61803 0H4.38197C4.76074 0 5.107 0.214002 5.27639 0.552786L9.77639 9.55279C9.91716 9.83431 9.91716 10.1657 9.77639 10.4472L5.27639 19.4472C5.107 19.786 4.76074 20 4.38197 20H1.61803C0.874652 20 0.391156 19.2177 0.723607 18.5528L4.77639 10.4472C4.91716 10.1657 4.91716 9.83431 4.77639 9.55279L0.723608 1.44722Z" />
    </marker>;

const Render = React.memo<RenderProps>(({ layout, color, strokeWidth, strokeStyle, selected, targeted }) => {

    const { base, start, end, body, overflow, spine } = layout;

    const o = base.topLeft();
    const [id1, id2] = [uuid(), uuid()];

    const cheveron = strokeStyle === ConnectorStrokeStyle.cheveron;

    const bodyWidth = cheveron ? 0 : strokeWidth;
    const bodyDash = (
        (strokeStyle === ConnectorStrokeStyle.dashed ? [strokeWidth * 3, Math.round(strokeWidth * 4)].join(' ') : null) ??
        (strokeStyle === ConnectorStrokeStyle.dotted ? [1, Math.round(strokeWidth * 2)].join(' ') : null) ??
        null
    );

    const sampledBody = cheveron
        ? Polyline.from(body.sample(body.length / strokeWidth)).toPath(false)
        : body;

    const padding = 5;
    const paddedBase = base.inflate(padding);
    return <RenderSvg area={paddedBase} /* style={{ boxShadow: '0 0 0 0px red' }} */>
        <defs>
            {cheveron && <RenderCheveronMarker id={id2} color={color} strokeWidth={strokeWidth} />}
        </defs>
        <g transform={`translate(${-o.x + padding} ${-o.y + padding})`} fill="none" stroke={color} strokeLinejoin="round" strokeLinecap="round">
            <path id={id1} strokeWidth={bodyWidth} strokeDasharray={bodyDash} d={sampledBody.toString()}
                markerStart={`url(#${id2})`}
                markerEnd={`url(#${id2})`}
                markerMid={`url(#${id2})`}
            />
            {!!start && <path strokeWidth={strokeWidth} fill={start.closed ? color : 'none'} d={start.toString()} />}
            {!!end && <path strokeWidth={strokeWidth} fill={end.closed ? color : 'none'} d={end.toString()} />}
            {!!overflow && !!selected && <path strokeWidth={2} strokeDasharray="5,5" fill={color} d={overflow.toString()} />}
            {!!(selected || targeted) && <path strokeWidth={Math.max(1, bodyWidth / 3)} d={(selected ? spine : body).toString()} stroke={Colors.primaryLight} />}
        </g>
    </RenderSvg>;
});
